<template>
  <v-card-text class="pt-6 pb-6">
    <div class="mb-4 text-center">
      <v-icon size="45" :color="currentCardLocked ? 'error' : 'purple'">
        {{ currentCardLocked ? 'mdi-lock' : 'mdi-lock-smart' }}
      </v-icon>
      
      <v-chip
        v-if="currentCardUid"
        class="mt-2"
        :color="currentCardLocked ? 'error' : 'success'"
        outlined
      >
        وضعیت کارت فعلی: {{ currentCardLocked ? 'قفل شده' : 'آزاد' }}
      </v-chip>
      <p v-else class="text-muted text-body-2 mt-2">
        ابتدا کارت را روی دستگاه قرار دهید...
      </p>
    </div>

    <!-- بخش مدیریت قفل -->
    <v-card outlined class="pa-4 mb-4" style="background-color: rgba(255,255,255,0.02);">
      <h4 class="mb-3 text-subtitle-1"><v-icon left color="primary">mdi-key</v-icon> تنظیمات امنیت و قفل کارت</h4>
      
      <v-radio-group v-model="cardType" row dense hide-details class="mb-4 justify-center">
        <v-radio label="کارت NTAG (۴ کاراکتر)" value="NTAG" color="primary"></v-radio>
        <v-radio label="کارت Mifare (۶ کاراکتر)" value="MIFARE" color="primary"></v-radio>
      </v-radio-group>
      
      <v-divider class="mb-4"></v-divider>

      <!-- اضافه شدن حالت Read-Only به منو -->
      <v-radio-group v-model="lockMode" row dense hide-details class="mb-4 justify-center">
        <v-radio label="رمز گذاشتن" value="lock" color="error"></v-radio>
        <v-radio label="باز کردن رمز" value="unlock" color="success"></v-radio>
        <v-radio label="قفل دائمی (بدون رمز)" value="readonly" color="warning"></v-radio>
      </v-radio-group>

      <!-- در حالت قفل دائمی نیازی به وارد کردن پسورد نیست -->
      <v-expand-transition>
        <div v-if="lockMode !== 'readonly'">
          <v-text-field
            v-model="password"
            :label="`پسورد (${passwordMaxLength} کاراکتر)`"
            outlined
            dense
            dir="ltr"
            prepend-inner-icon="mdi-key"
            :maxlength="passwordMaxLength"
            :counter="passwordMaxLength"
            :rules="passwordRules"
          ></v-text-field>
        </div>
      </v-expand-transition>

      <v-alert v-if="lockMode === 'readonly'" type="warning" dense text class="mb-4 text-caption">
        توجه: قفل دائمی (Read-Only) در کارت‌های NTAG بازگشت‌ناپذیر است و کارت دیگر قابل فرمت یا ویرایش نخواهد بود!
      </v-alert>

      <v-btn
        block
        elevation="2"
        :class="getBtnColor"
        class="rounded-lg mt-2 white--text"
        :loading="isProcessing"
        :disabled="!isBtnEnabled"
        @click="executeAction"
      >
        <v-icon class="mx-2" right>{{ getBtnIcon }}</v-icon>
        {{ getBtnText }}
      </v-btn>
    </v-card>
  </v-card-text>
</template>

<script>
export default {
  name: "NfcLockManager",
  props: {
    socket: { type: Object, required: true },
    isConnected: { type: Boolean, default: false }
  },
  data() {
    return {
      cardType: 'NTAG',
      lockMode: 'lock',
      password: '',
      isProcessing: false,
      currentCardUid: null,
      currentCardLocked: false,
    };
  },
  computed: {
    passwordMaxLength() {
      return this.cardType === 'MIFARE' ? 6 : 4;
    },
    isValidPassword() {
      if (this.lockMode === 'readonly') return true; // برای قفل دائمی رمز نیاز نیست
      return this.password.length === this.passwordMaxLength;
    },
    isBtnEnabled() {
      return this.isValidPassword && this.isConnected;
    },
    passwordRules() {
      return [
        v => v.length === this.passwordMaxLength || `پسورد باید دقیقاً ${this.passwordMaxLength} کاراکتر باشد`
      ];
    },
    getBtnColor() {
      if (this.lockMode === 'lock') return 'btn-delete';
      if (this.lockMode === 'unlock') return 'btn-get-code';
      return 'btn-edit';
    },
    getBtnIcon() {
      if (this.lockMode === 'lock') return 'mdi-lock';
      if (this.lockMode === 'unlock') return 'mdi-lock-open-variant';
      return 'mdi-lock-check';
    },
    getBtnText() {
      if (this.lockMode === 'lock') return 'اعمال رمز روی کارت';
      if (this.lockMode === 'unlock') return 'حذف رمز کارت';
      return 'اعمال قفل دائمی روی کارت';
    }
  },
  watch: {
    cardType() { this.password = ''; },
    lockMode() { this.password = ''; }
  },
  mounted() {
    this.socket.on("nfc-uid", (data) => {
      this.currentCardUid = data.uid;
      this.cardType = data.type === 'MIFARE_CLASSIC' ? 'MIFARE' : 'NTAG';
      this.currentCardLocked = data.is_locked;
    });

    this.socket.on("nfc-lock-status", (res) => {
      this.isProcessing = false;
      if (res.status === 'success') {
        this.$toast.success(res.message);
        this.password = '';
        this.$emit('status-update', "عملیات موفقیت‌آمیز بود.");
      } else {
        this.$toast.error(res.message || "خطا در عملیات");
        this.$emit('status-update', res.message);
      }
    });
  },
  methods: {
    textToHex(text) {
      return text.split('').map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('').toUpperCase();
    },
    executeAction() {
      if (!this.isValidPassword) return;
      this.isProcessing = true;
      
      let eventName = 'nfc-lock';
      if (this.lockMode === 'unlock') eventName = 'nfc-unlock';
      if (this.lockMode === 'readonly') eventName = 'nfc-readonly';

      let statusMsg = 'در حال ارتباط با کارت...';
      this.$emit('status-update', statusMsg);

      let payload = {};
      if (this.lockMode !== 'readonly') {
        payload.password = this.cardType === 'MIFARE' ? this.textToHex(this.password) : this.password;
      }

      this.socket.emit(eventName, payload);
    }
  }
};
</script>
